// jobtodoSlice.ts
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { JobTodo } from '../types/entities';
import { RootState } from './store';
import { baseUrlApi } from '../../utils/url';

interface JobTodoState {
  jobtodos: JobTodo[];
  jobtodo: JobTodo | null;
  encoursjobtodo: number[];
  loading: boolean;
  error: string | null;
}

const initialState: JobTodoState = {
  jobtodos: [],
  jobtodo: null,
  encoursjobtodo: [],
  loading: false,
  error: null,
};

export const fetchJobtodos = createAsyncThunk<JobTodo[], void, { state: RootState }>(
  'jobtodo/fetchJobtodos',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      const response = await axios.get(`${baseUrlApi}/jobtodo`, {
        headers: { Authorization: `bearer ${authentification.user.token}` }
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch jobtodos');
    }
  }
);

export const fetchJobtodo = createAsyncThunk<JobTodo, number, { state: RootState }>(
  'jobtodo/fetchJobtodo',
  async (jobtodoId, { getState, dispatch, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      const response = await axios.get(`${baseUrlApi}/jobtodo/${jobtodoId}`, {
        headers: { Authorization: `bearer ${authentification.user.token}` }
      });
      dispatch(fetchJobtodos());
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch jobtodo');
    }
  }
);

export const createJobtodo = createAsyncThunk<JobTodo, Partial<JobTodo>, { state: RootState }>(
  'jobtodo/createJobtodo',
  async (jobtodoData, { getState, dispatch, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      const response = await axios.post(`${baseUrlApi}/jobtodo`, jobtodoData, {
        headers: { Authorization: `bearer ${authentification.user.token}` }
      });
      dispatch(fetchJobtodos());
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create jobtodo');
    }
  }
);

export const updateJobtodo = createAsyncThunk<
  JobTodo,
  { jobtodoData: Partial<JobTodo>; jobtodoId: number },
  { state: RootState }
>(
  'jobtodo/updateJobtodo',
  async ({ jobtodoData, jobtodoId }, { getState, dispatch, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      const response = await axios.put(`${baseUrlApi}/jobtodo/${jobtodoId}`, jobtodoData, {
        headers: { Authorization: `bearer ${authentification.user.token}` }
      });
      dispatch(fetchJobtodos());
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update jobtodo');
    }
  }
);

export const deleteJobtodo = createAsyncThunk<number, number, { state: RootState }>(
  'jobtodo/deleteJobtodo',
  async (jobtodoId, { getState, dispatch, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      await axios.delete(`${baseUrlApi}/jobtodo/${jobtodoId}`, {
        headers: { Authorization: `bearer ${authentification.user.token}` }
      });
      dispatch(fetchJobtodos());
      return jobtodoId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete jobtodo');
    }
  }
);

const jobtodoSlice = createSlice({
  name: 'jobtodo',
  initialState,
  reducers: {
    clearJobtodo: (state) => { 
      state.jobtodo = null; 
    },
    clearJobtodos: (state) => { 
      state.jobtodos = []; 
    },
    resetJobtodoState: () => initialState,
    encoursJobtodo: (state, action) => {
      state.encoursjobtodo = [action.payload];
    },
  },
  extraReducers: (builder) => {
    [fetchJobtodos, fetchJobtodo, createJobtodo, updateJobtodo, deleteJobtodo].forEach((thunk) => {
      builder
        .addCase(thunk.pending, (state) => {
          state.loading = true;
          state.error = null;
        })
        .addCase(thunk.rejected, (state, action) => {
          state.loading = false;
          state.error = action.payload as string;
        });
    });

    builder
      .addCase(fetchJobtodos.fulfilled, (state, action) => {
        state.loading = false;
        state.jobtodos = action.payload;
      })
      .addCase(fetchJobtodo.fulfilled, (state, action) => {
        state.loading = false;
        state.jobtodo = action.payload;
      })
      .addCase(createJobtodo.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateJobtodo.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(deleteJobtodo.fulfilled, (state, action) => {
        state.loading = false;
        state.jobtodos = state.jobtodos.filter(item => item.id !== action.payload);
      });
  },
});

export const { clearJobtodo, clearJobtodos, resetJobtodoState, encoursJobtodo } = jobtodoSlice.actions;
export default jobtodoSlice.reducer;